import { deleteFridge, deleteSensor, useFridges, useSensors } from '@gozoki/api';
import { Button, Paper, Tab, Tabs, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ConfirmButton } from '../../components/communication/ConfirmButton';
import PlusIcon from '../../components/icons/PlusIcon';
import Page from '../../components/Page';
import { ROUTE_BUILDER, SENSORS_ROUTES } from '../../utils/constants/routes.constants';
import { StoresPicker } from '../../utils/dashboard/storesPicker';
import { useRights } from '../../utils/hooks';
import { fonts } from '../../utils/theme/fonts.theme';
import { ButtonOverrides } from '../../utils/theme/overrides.theme';
import FridgeGrid from './FridgeGrid';
import SensorChart from './SensorChart';
import SensorGrid from './SensorGrid';
import SensorStatusCards from './SensorStatusCards';

const SensorsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const [currentStore, setCurrentStore] = useState<number | null>(null);
    const [selectedSensorIds, setSelectedSensorIds] = useState<number[]>([]);
    const [selectedFridgeIds, setSelectedFridgeIds] = useState<number[]>([]);
    const [currentPageSensorIds, setCurrentPageSensorIds] = useState<number[]>([]);

    // Initialize tab from URL or default to 0
    const [currentTab, setCurrentTab] = useState(() => {
        const tabParam = searchParams.get('tab');
        return tabParam ? parseInt(tabParam, 10) : 0;
    });
    const { data: sensors, isLoading } = useSensors(currentStore);
    const { data: fridges, isLoading: isLoadingFridges } = useFridges(currentStore);
    const isCpiGlobalOrSuperAdmin = useRights(['CPI_GLOBAL', 'SUPER_ADMIN']);
    const navigate = useNavigate();

    // Get temperature thresholds (using default values for now)
    const temperatureThresholdMin = 0;
    const temperatureThresholdMax = 6;

    // Get sensor IDs from selected fridges
    const sensorIdsFromFridges = useMemo(() => {
        if (selectedFridgeIds.length === 0 || !fridges || !sensors) {
            return [];
        }

        // Get fridge references from selected fridge IDs
        const selectedFridgeReferences = fridges
            .filter((fridge) => selectedFridgeIds.includes(fridge.id))
            .map((fridge) => fridge.reference);

        // Get sensor IDs that belong to selected fridges
        return sensors
            .filter(
                (sensor) =>
                    sensor.fridge && selectedFridgeReferences.includes(sensor.fridge.reference)
            )
            .map((sensor) => sensor.id);
    }, [selectedFridgeIds, fridges, sensors]);

    // Combine selected sensor IDs with sensors from selected fridges
    const allSelectedSensorIds = useMemo(() => {
        const combined = [...selectedSensorIds, ...sensorIdsFromFridges];
        // Remove duplicates
        return Array.from(new Set(combined));
    }, [selectedSensorIds, sensorIdsFromFridges]);

    // Update URL when tab changes
    useEffect(() => {
        const params: Record<string, string> = {};
        if (currentTab !== 0) {
            params.tab = currentTab.toString();
        }
        setSearchParams(params, { replace: true });
        setSelectedSensorIds([]);
        setSelectedFridgeIds([]);
    }, [currentTab, setSearchParams]);

    const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    // Delete mutations
    const deleteSensorMut = useMutation({
        mutationFn: deleteSensor,
        onSuccess: () => {
            // Invalidate sensors queries to refetch the list
            queryClient.invalidateQueries({ queryKey: ['sensors'] });
            setSelectedSensorIds([]);
        },
    });

    const deleteFridgeMut = useMutation({
        mutationFn: deleteFridge,
        onSuccess: () => {
            // Invalidate fridges query to refetch the list
            queryClient.invalidateQueries({ queryKey: ['fridges'] });
            setSelectedFridgeIds([]);
        },
    });

    return (
        <Page>
            <div style={{ display: 'flex', paddingLeft: 10, paddingBottom: 10 }}>
                <Typography style={fonts.pageTitle} flexGrow={1}>
                    Équipements de Réfrigération
                </Typography>
                {currentTab === 0 && selectedSensorIds.length === 1 && (
                    <>
                        <ConfirmButton
                            buttonText="Supprimer ce capteur"
                            buttonProps={{
                                variant: 'outlined',
                                sx: {
                                    marginRight: '16px',
                                    height: '42px',
                                    ...ButtonOverrides.redOutlined,
                                },
                                disabled: !isCpiGlobalOrSuperAdmin || deleteSensorMut.isPending,
                            }}
                            description="Etes-vous sûrs de vouloir supprimer ce capteur ? Cette action est irréversible et supprimera toutes les données associées à ce capteur."
                            onConfirm={() => {
                                return deleteSensorMut.mutateAsync(selectedSensorIds[0]);
                            }}
                            title="Supprimer ce capteur ?"
                            confirmButton={
                                deleteSensorMut.isPending
                                    ? {
                                          loading: true,
                                          text: 'Suppression en cours     ',
                                      }
                                    : { text: 'Supprimer' }
                            }
                        />
                        <Button
                            variant="contained"
                            sx={{ marginRight: '16px' }}
                            disabled={!isCpiGlobalOrSuperAdmin}
                            onClick={() =>
                                navigate(ROUTE_BUILDER.updateSensor(selectedSensorIds[0]))
                            }
                        >
                            Modifier ce capteur
                        </Button>
                    </>
                )}
                {currentTab === 1 && selectedFridgeIds.length === 1 && (
                    <>
                        <ConfirmButton
                            buttonText="Supprimer ce réfrigérateur"
                            buttonProps={{
                                variant: 'outlined',
                                sx: {
                                    marginRight: '16px',
                                    height: '42px',
                                    ...ButtonOverrides.redOutlined,
                                },
                                disabled: !isCpiGlobalOrSuperAdmin || deleteFridgeMut.isPending,
                            }}
                            description="Etes-vous sûrs de vouloir supprimer ce réfrigérateur ? Cette action est irréversible et supprimera toutes les données associées à ce réfrigérateur."
                            onConfirm={() => {
                                return deleteFridgeMut.mutateAsync(selectedFridgeIds[0]);
                            }}
                            title="Supprimer ce réfrigérateur ?"
                            confirmButton={
                                deleteFridgeMut.isPending
                                    ? {
                                          loading: true,
                                          text: 'Suppression en cours     ',
                                      }
                                    : { text: 'Supprimer' }
                            }
                        />
                        <Button
                            variant="contained"
                            sx={{ marginRight: '16px' }}
                            disabled={!isCpiGlobalOrSuperAdmin}
                            onClick={() =>
                                navigate(
                                    `${ROUTE_BUILDER.updateFridge(selectedFridgeIds[0])}?tab=1`
                                )
                            }
                        >
                            Modifier ce réfrigérateur
                        </Button>
                    </>
                )}
                {currentTab === 0 && (
                    <Button
                        variant="contained"
                        sx={{ marginRight: '16px' }}
                        disabled={!isCpiGlobalOrSuperAdmin}
                        onClick={() => navigate(SENSORS_ROUTES.ADD_SENSOR)}
                    >
                        <PlusIcon />
                        Ajouter un capteur
                    </Button>
                )}
                {currentTab === 1 && (
                    <Button
                        variant="contained"
                        sx={{ marginRight: '16px' }}
                        disabled={!isCpiGlobalOrSuperAdmin}
                        onClick={() => navigate(`${SENSORS_ROUTES.ADD_FRIDGE}?tab=1`)}
                    >
                        <PlusIcon />
                        Ajouter un réfrigérateur
                    </Button>
                )}
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
                <StoresPicker
                    setUniqueStore={setCurrentStore}
                    allowAllStores
                    onlyBoutiques
                    defaultStore={currentStore !== null ? currentStore : undefined}
                />
            </div>
            <SensorStatusCards sensors={sensors} />
            <Tabs value={currentTab} onChange={handleChange} sx={{ marginBottom: 1 }}>
                <Tab label="Capteurs" />
                <Tab label="Réfrigérateurs" />
            </Tabs>
            <Paper
                sx={{
                    marginTop: '16px',
                }}
            >
                {currentTab === 0 && (
                    <SensorGrid
                        data={sensors}
                        loading={isLoading && !sensors}
                        onSelectionChange={setSelectedSensorIds}
                        onPageChange={setCurrentPageSensorIds}
                    />
                )}
                {currentTab === 1 && (
                    <FridgeGrid
                        data={fridges}
                        loading={isLoadingFridges && !fridges}
                        onSelectionChange={setSelectedFridgeIds}
                    />
                )}
            </Paper>
            <SensorChart
                selectedSensorIds={allSelectedSensorIds}
                isFridgeSelected={selectedFridgeIds.length > 0}
                storeId={currentStore}
                temperatureThresholdMin={temperatureThresholdMin}
                temperatureThresholdMax={temperatureThresholdMax}
                currentPageSensorIds={currentPageSensorIds}
            />
            <div style={{ margin: 10 }}>
                <Typography variant="body2" color="text.secondary" align="center">
                    {currentTab === 0
                        ? 'Sélectionnez des capteurs dans le tableau pour afficher leur historique'
                        : "Sélectionnez des réfrigérateurs dans le tableau pour afficher l'historique de leurs capteurs"}
                </Typography>
            </div>
        </Page>
    );
};

export default SensorsPage;
